import { Box, Stack } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { editProjectPlaceholders, Placeholder, qk } from 'api'
import { DialogContent, ToastContent } from 'elements/feedback'
import { Select } from 'elements/inputs'
import { getInputError } from 'lib/form-utils'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import {
  generalPlaceholders,
  namedPlaceholders,
} from '../../project-terms/components/import-export/placeholders'

const formId = 'EDIT_PLACEHOLDERS_FORM'

type FormValues = {
  generalPlaceholder: string
  namedPlaceholder: string
}

type EditPlaceholdersFormProps = {
  projectId: string
  placeholders: Placeholder
  onClose: () => void
}

export const EditPlaceholdersForm = ({
  projectId,
  placeholders,
  onClose,
}: EditPlaceholdersFormProps) => {
  const { t } = useTranslation()

  const queryClient = useQueryClient()

  const $editProjectPlaceholders = useMutation(editProjectPlaceholders)

  const {
    control,
    handleSubmit,

    formState: { isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      generalPlaceholder: placeholders.base,
      namedPlaceholder: placeholders.namedLeft + ' ' + placeholders.namedRight,
    },
  })

  return (
    <DialogContent
      title={t('projects.terms.placeholders.change_placeholders')}
      isActionLoading={$editProjectPlaceholders.isLoading}
      externalFormId={formId}
      onDeny={onClose}
      denyText={t('common.cancel')}
      isConfirmDisabled={!isDirty}
    >
      <Box
        id={formId}
        component="form"
        onSubmit={handleSubmit(({ generalPlaceholder, namedPlaceholder }) =>
          $editProjectPlaceholders.mutate(
            {
              projectId,

              ...(generalPlaceholder !== '' && {
                base: generalPlaceholder,
              }),

              ...(namedPlaceholder !== '' && {
                namedLeft: namedPlaceholder.slice(
                  0,
                  namedPlaceholder.indexOf(' '),
                ),
                namedRight: namedPlaceholder.slice(
                  namedPlaceholder.indexOf(' ') + 1,
                ),
              }),
            },
            {
              onSuccess: () => {
                onClose()
                queryClient.invalidateQueries(
                  qk.projects.details.toKeyWithArgs({ projectId }),
                )
                toast.success(
                  <ToastContent
                    title={t(
                      'projects.terms.placeholders.placeholders_successfully_changed',
                    )}
                  />,
                )
              },
            },
          ),
        )}
      >
        <Stack direction="row" spacing={3}>
          <Controller
            name="generalPlaceholder"
            control={control}
            render={({ field, fieldState }) => (
              <Select
                label={t(
                  'projects.terms.placeholders.select_general_placeholder',
                )}
                options={generalPlaceholders}
                {...getInputError(fieldState.error)}
                {...field}
              />
            )}
          />

          <Controller
            name="namedPlaceholder"
            control={control}
            render={({ field, fieldState }) => (
              <Select
                label={t(
                  'projects.terms.placeholders.select_named_placeholder',
                )}
                options={namedPlaceholders}
                {...getInputError(fieldState.error)}
                {...field}
              />
            )}
          />
        </Stack>
      </Box>
    </DialogContent>
  )
}
